import api from './api';

/**
 * Landelijke Crisis-Pool & Garantiefonds (/api/crisis).
 * Opdrachtgever: abonnement per vestiging, No-Claim tegoed, alarmknop.
 * Professional: onboarding, rooster (Token-Engine), tokenwaarde, alarmen.
 */

export interface CrisisTier {
  id: number;
  schil_niveau: number;
  naam: string;
  min_medewerkers: number;
  max_medewerkers: number;
  maandelijkse_fee: number | string;
  basis_vrije_uitrukken: number;
  max_no_claim_cap: number;
}

export interface CrisisRegio {
  code: string;
  naam: string;
  omschrijving: string;
  buurregios: string[];
}

export interface CrisisAbonnement {
  id: string;
  location_id: string;
  vestiging_naam: string;
  vestiging_postcode: string | null;
  regio_code: string;
  status: 'PENDING_MANDATE' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED';
  aantal_medewerkers: number;
  schil_niveau: number;
  schil_naam: string;
  maandelijkse_fee: number;
  basis_vrije_uitrukken: number;
  max_no_claim_cap: number;
  kalenderjaar: number | null;
  huidige_vrije_uitrukken: number;
  overgedragen_vrije_uitrukken: number;
  gebruikte_uitrukken: number;
  beschikbare_uitrukken: number;
  totaal_ingelegd: number;
  fonds_ingelegd: number;
  pool_ingelegd: number;
  actief_sinds: string | null;
  created_at: string;
  sector: CrisisSectorCode | null;
  ploegendienst: boolean;
  contact_naam: string | null;
  contact_telefoon: string | null;
  addendum_versie: string | null;
  addendum_getekend_op: string | null;
}

export type CrisisSectorCode = 'KANTOOR_SAAS' | 'ZORG_WELZIJN' | 'LOGISTIEK_DISTRIBUTIE' | 'PRODUCTIE_INDUSTRIE';

export interface CrisisSector {
  code: CrisisSectorCode;
  label: string;
  omschrijving: string;
}

/** Dekking van een alarm: VOLLEDIG (alles Garantiefonds), GEDEELTELIJK (eerste N), GEEN (alles verzamelfactuur) */
export interface CrisisDekking {
  type: 'VOLLEDIG' | 'GEDEELTELIJK' | 'GEEN';
  gewenst: number;
  gedekt: number;
  gefactureerd: number;
}

export interface CrisisAlarmResponse {
  id: string;
  responder_naam: string;
  status: string;
  volgnummer: number | null;
  geaccepteerd_op: string;
  uren: number | null;
  uren_gefactureerd: number | null;
  bedrag_bruto: number | null;
  gedekt_door: 'FONDS' | 'FACTUUR' | null;
}

export interface CrisisAlarm {
  bron?: 'COCKPIT' | 'MEDEWERKER';
  gemeld_door_pseudoniem?: string | null;
  id: string;
  subscription_id: string;
  location_id: string | null;
  vestiging_naam: string | null;
  regio_code: string;
  response_type: 'SINGLE' | 'DUAL';
  aantal_gewenst: number;
  omschrijving: string | null;
  status: 'OPEN' | 'ACCEPTED' | 'CLOSED' | 'CANCELLED';
  escalatie_niveau: number;
  uit_tegoed: boolean;
  punten_afgeboekt: number;
  dekking: CrisisDekking;
  chat_conversation_id: string | null;
  video_url: string | null;
  created_at: string;
  geaccepteerd_op: string | null;
  gesloten_op: string | null;
  responses: CrisisAlarmResponse[];
}

export interface CrisisEmployerOverzicht {
  tiers: CrisisTier[];
  regios: CrisisRegio[];
  locaties: Array<{ id: string; naam: string; postcode: string | null; plaats: string | null; is_hoofdvestiging: boolean; regio_code: string | null }>;
  sectoren: CrisisSector[];
  addendum_versie: string;
  abonnementen: CrisisAbonnement[];
  alarmen: CrisisAlarm[];
  fonds_saldo_landelijk: number;
  tarieven: { uurtarief: number; min_uren: number; split: { BELAGOM_FEE: number; POOL_SHARE: number; FONDS_SHARE: number } };
}

export const fetchCrisisEmployerOverzicht = async (): Promise<CrisisEmployerOverzicht> => {
  const { data } = await api.get('/crisis/employer/overzicht');
  return data.data;
};

export const startCrisisAbonnement = async (locationId: string, aantalMedewerkers: number): Promise<{ subscriptionId: string; checkoutUrl: string; schil: CrisisTier }> => {
  const { data } = await api.post('/crisis/employer/abonnement', { location_id: locationId, aantal_medewerkers: aantalMedewerkers });
  return data.data;
};

// ---- Calamiteiten-onboarding wizard (3 stappen) ----------------------------

export interface CrisisWizardVoorbereiding {
  subscriptionId: string;
  screening: { sector: CrisisSectorCode; ploegendienst: boolean; contact_naam: string; contact_telefoon: string; telefoon_masker: string };
  overzicht: {
    schil: CrisisTier;
    vestiging: { id: string; naam: string; postcode: string | null; plaats: string | null; aantal_medewerkers: number };
    regio_code: string;
    retainer_per_maand: number;
    basis_vrije_uitrukken: number;
    max_no_claim_cap: number;
    uurtarief: number;
    min_uren: number;
    split: { BELAGOM_FEE: number; POOL_SHARE: number; FONDS_SHARE: number };
  };
  addendum: { versie: string; tekst: string; hash: string };
}

export const bereidCrisisWizardVoor = async (invoer: {
  location_id: string;
  aantal_medewerkers: number;
  sector: CrisisSectorCode;
  ploegendienst: boolean;
  contact_naam: string;
  contact_telefoon: string;
}): Promise<CrisisWizardVoorbereiding> => {
  const { data } = await api.post('/crisis/employer/wizard/voorbereiden', invoer);
  return data.data;
};

export const vraagCrisisAddendumCode = async (subscriptionId: string): Promise<{ telefoon_masker: string }> => {
  const { data } = await api.post(`/crisis/employer/wizard/${subscriptionId}/teken-code`);
  return data.data;
};

export const ondertekenCrisisAddendum = async (
  subscriptionId: string,
  code: string
): Promise<{ status: 'ACTIVE' | 'PENDING_MANDATE'; subscriptionId: string; checkoutUrl: string | null; schil: CrisisTier }> => {
  const { data } = await api.post(`/crisis/employer/wizard/${subscriptionId}/onderteken`, { code, akkoord: true });
  return data.data;
};

export const zegCrisisAbonnementOp = async (id: string): Promise<void> => {
  await api.post(`/crisis/employer/abonnement/${id}/opzeggen`);
};

export const maakCrisisAlarm = async (locationId: string, responseType: 'SINGLE' | 'DUAL', omschrijving?: string): Promise<CrisisAlarm> => {
  const { data } = await api.post('/crisis/employer/alarm', { location_id: locationId, response_type: responseType, omschrijving: omschrijving || null });
  return data.data;
};

export const sluitCrisisAlarm = async (id: string): Promise<CrisisAlarm> => {
  const { data } = await api.post(`/crisis/employer/alarm/${id}/sluit`);
  return data.data;
};

// ---------------------------------------------------------------------------
// Professional
// ---------------------------------------------------------------------------

export interface CrisisResponder {
  user_id: string;
  professional_type: string | null;
  big_nummer: string | null;
  licentie_nummer: string | null;
  certificaten: string[];
  verzekering_url: string | null;
  verzekering_dekking: number | null;
  regio_code: string;
  postcode: string | null;
  telefoon: string | null;
  status: 'AANGEMELD' | 'OVEREENKOMST_OPEN' | 'ACTIEF' | 'GESCHORST';
  is_crisis_responder: boolean;
  overeenkomst_getekend_op: string | null;
}

export interface CrisisResponderStatus {
  responder: CrisisResponder | null;
  suggestie: {
    postcode: string | null;
    regio_code: string | null;
    telefoon: string | null;
    verzekering_url: string | null;
    verzekering_dekking: number | null;
    professional_types: string[];
  };
  stripe_verified: boolean;
  regios: CrisisRegio[];
}

export interface CrisisRoosterShift {
  shift: 'dag' | 'avond' | 'nacht';
  label: string;
  tokens: number;
  is_weekend: boolean;
  capaciteit: number;
  geboekt: number;
  vol: boolean;
  mijn_slot_id: string | null;
  verleden: boolean;
}

export interface CrisisRooster {
  regio_code: string;
  jaar: number;
  maand: number;
  dagen: Array<{ datum: string; weekdag: number; shifts: CrisisRoosterShift[] }>;
  mijn_tokens: number;
}

export interface CrisisTokenDashboard {
  periode: string;
  regio_code: string | null;
  pool_deze_maand: number;
  inleg_deze_maand: number;
  actieve_abonnementen: number;
  tokens_geboekt_landelijk: number;
  tokens_geboekt_regio: number;
  tokens_max_landelijk: number;
  tokens_max_regio: number;
  mijn_tokens: number;
  token_waarde_prognose: number | null;
  token_waarde_bij_max_bezetting: number | null;
  mijn_prognose: number;
  historie: Array<{
    periode: string;
    pool_totaal: number;
    totaal_tokens: number;
    token_waarde: number;
    aantal_responders: number;
    status: string;
    mijn_tokens: number;
    mijn_bedrag: number;
    mijn_status: string | null;
  }>;
  staffel: Array<{ shift: string; label: string; factor: number; tokens_per_shift: number }>;
  weekend_factor: number;
}

export interface CrisisAlarmVoorResponder {
  id: string;
  status: 'OPEN' | 'ACCEPTED' | 'CLOSED' | 'CANCELLED';
  response_type: 'SINGLE' | 'DUAL';
  aantal_gewenst: number;
  aantal_geaccepteerd: number;
  regio_code: string;
  escalatie_niveau: number;
  omschrijving: string | null;
  bedrijfsnaam: string | null;
  vestiging_naam: string | null;
  vestiging_plaats: string | null;
  vestiging_adres: string | null;
  video_url: string | null;
  uit_tegoed: boolean;
  dekking: CrisisDekking;
  /** Dual-Response: interne duo-chat zodra beide professionals hebben geaccepteerd */
  chat_conversation_id: string | null;
  duo_partner: { naam: string; telefoon: string | null; volgnummer: number } | null;
  created_at: string;
  mijn_response: {
    id: string;
    status: string;
    volgnummer: number | null;
    uren: number | null;
    uren_gefactureerd: number | null;
    zzp_netto: number | null;
    gedekt_door: string | null;
    uitbetaling_status: string;
  } | null;
}

export const fetchCrisisResponderStatus = async (): Promise<CrisisResponderStatus> => {
  const { data } = await api.get('/crisis/professional/status');
  return data.data;
};

export const meldCrisisResponderAan = async (invoer: {
  postcode: string;
  big_nummer?: string;
  licentie_nummer?: string;
  professional_type?: string | null;
  certificaten?: string[];
  verzekering_url?: string | null;
  verzekering_dekking: number;
  telefoon?: string;
}): Promise<CrisisResponderStatus> => {
  const { data } = await api.post('/crisis/professional/aanmelden', invoer);
  return data.data;
};

export const fetchCrisisOvereenkomst = async (): Promise<string> => {
  const { data } = await api.get('/crisis/professional/overeenkomst');
  return data.data.tekst;
};

export const vraagCrisisTekencode = async (): Promise<{ telefoon_masker: string }> => {
  const { data } = await api.post('/crisis/professional/overeenkomst/teken-code');
  return data.data;
};

export const ondertekenCrisisOvereenkomst = async (code: string): Promise<CrisisResponderStatus> => {
  const { data } = await api.post('/crisis/professional/overeenkomst/onderteken', { code });
  return data.data;
};

export const fetchCrisisRooster = async (jaar: number, maand: number): Promise<CrisisRooster> => {
  const { data } = await api.get('/crisis/professional/rooster', { params: { jaar, maand } });
  return data.data;
};

export const boekCrisisShift = async (datum: string, shift: 'dag' | 'avond' | 'nacht') => {
  const { data } = await api.post('/crisis/professional/rooster', { datum, shift });
  return data.data;
};

export const annuleerCrisisShift = async (slotId: string) => {
  await api.delete(`/crisis/professional/rooster/${slotId}`);
};

export const fetchCrisisTokenDashboard = async (): Promise<CrisisTokenDashboard> => {
  const { data } = await api.get('/crisis/professional/tokens');
  return data.data;
};

export const fetchCrisisAlarmenVoorResponder = async (): Promise<CrisisAlarmVoorResponder[]> => {
  const { data } = await api.get('/crisis/professional/alarmen');
  return data.data;
};

export interface CrisisAccepteerResultaat {
  alarm_id: string;
  video_url: string;
  aantal_geaccepteerd: number;
  aantal_gewenst: number;
  volgnummer: number;
  gedekt_door: 'FONDS' | 'FACTUUR';
  chat_conversation_id: string | null;
  duo_partner: { naam: string; telefoon: string | null } | null;
}

export const accepteerCrisisAlarm = async (id: string): Promise<CrisisAccepteerResultaat> => {
  const { data } = await api.post(`/crisis/professional/alarm/${id}/accepteer`);
  return data.data;
};

export const dienCrisisUrenIn = async (id: string, uren: number, reiskosten: number) => {
  const { data } = await api.post(`/crisis/professional/alarm/${id}/uren`, { uren, reiskosten });
  return data.data;
};

export const euroCrisis = (v: number | string | null | undefined, decimals = 2) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(Number(v ?? 0));

// ---- Medewerkersportaal: rode alarmknop -------------------------------------

export interface MedewerkerCrisisAlarm {
  id: string;
  location_id: string;
  status: 'OPEN' | 'ACCEPTED' | 'CLOSED' | 'CANCELLED';
  response_type: 'SINGLE' | 'DUAL';
  video_url: string | null;
  created_at: string;
}

export interface MedewerkerCrisisStatus {
  beschikbaar: boolean;
  vestigingen: Array<{ location_id: string; naam: string; plaats: string | null; alarm_loopt: boolean }>;
  standaard_location_id: string | null;
  mijn_alarmen: MedewerkerCrisisAlarm[];
}

export const fetchMedewerkerCrisisStatus = async (): Promise<MedewerkerCrisisStatus> => {
  const { data } = await api.get('/crisis/medewerker/status');
  return data.data;
};

export const maakMedewerkerCrisisAlarm = async (invoer: {
  location_id: string | null;
  response_type: 'SINGLE' | 'DUAL';
  omschrijving?: string | null;
}): Promise<MedewerkerCrisisAlarm> => {
  const { data } = await api.post('/crisis/medewerker/alarm', invoer);
  return data.data;
};
